// src/pages/LandingPage.js
import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, CalendarDays, ShieldCheck, Users, ArrowRight, BadgeCheck } from 'lucide-react';
import { Btn, Card } from '../components/ui';
import PajNtaubBand from '../components/ui/PajNtaubBand';
import Footer from '../components/Footer';

const LandingPage = ({ openAuth }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const piliersRef = useRef(null);
  const traditionsRef = useRef(null);
  const [hoveredPillar, setHoveredPillar] = useState(null);

  const pillars = [
    {
      icon: Heart,
      title: t('landing.pillars.dating.title'),
      desc: t('landing.pillars.dating.desc'),
      color: '#e11d48',
      bgHover: 'from-rose-50 to-pink-50 dark:from-rose-950/30 dark:to-pink-950/30',
      iconBg: 'from-rose-300 to-rose-500',
      link: t('landing.pillars.dating.link'),
      action: openAuth,
    },
    {
      icon: Users,
      title: t('landing.pillars.community.title'),
      desc: t('landing.pillars.community.desc'),
      color: '#7c3aed',
      bgHover: 'from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30',
      iconBg: 'from-violet-300 to-violet-500',
      link: t('landing.pillars.community.link'),
      action: openAuth,
    },
    {
      icon: CalendarDays,
      title: t('landing.pillars.events.title'),
      desc: t('landing.pillars.events.desc'),
      color: '#f59e0b',
      bgHover: 'from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30',
      iconBg: 'from-amber-300 to-amber-500',
      link: t('landing.pillars.events.link'),
      action: () => navigate('/events'),
    },
  ];

  const traditions = [
    {
      icon: ShieldCheck,
      title: t('landing.traditions.clan.title'),
      desc: t('landing.traditions.clan.desc'),
      color: 'rose',
    },
    {
      icon: BadgeCheck,
      title: t('landing.traditions.verified.title'),
      desc: t('landing.traditions.verified.desc'),
      color: 'emerald',
    },
    {
      icon: Users,
      title: t('landing.traditions.safe.title'),
      desc: t('landing.traditions.safe.desc'),
      color: 'violet',
    },
  ];

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ paddingTop: 'calc(4rem + 7px)' }}
    >
      <Helmet>
        <title>{t('landing.meta.title')}</title>
        <meta name="description" content={t('landing.meta.description')} />
        <link rel="canonical" href="https://hmonghearts.com/" />
        <meta property="og:title" content={t('landing.meta.title')} />
        <meta property="og:description" content={t('landing.meta.description')} />
        <meta property="og:image" content="https://hmonghearts.com/og-default.png" />
        <meta property="og:url" content="https://hmonghearts.com/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'WebSite',
                '@id': 'https://hmonghearts.com/#website',
                url: 'https://hmonghearts.com',
                name: 'HmongHearts',
                description: 'La communauté Hmong : rencontres, événements et annonces.',
                inLanguage: 'fr-FR',
              },
              {
                '@type': 'Organization',
                '@id': 'https://hmonghearts.com/#organization',
                name: 'HmongHearts',
                alternateName: 'Archion Consulting',
                url: 'https://hmonghearts.com',
                logo: 'https://hmonghearts.com/logo-hmong.svg',
                contactPoint: {
                  '@type': 'ContactPoint',
                  email: 'contact@hmonghearts.com',
                  contactType: 'customer service',
                  availableLanguage: ['French', 'English'],
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <div className="relative z-10">
        {/* ── Hero Section ── */}
        <section
          data-testid="landing-hero"
          className="filigrane-kotaw relative overflow-hidden bg-gradient-to-b from-rose-50/50 via-pink-50/30 to-slate-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950"
        >
          <div className="container mx-auto px-4 py-10 sm:py-16 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
              {/* Texte hero */}
              <div className="lg:w-1/2 space-y-6 text-center lg:text-left">
                <div className="inline-flex px-4 sm:px-5 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/30 dark:to-pink-900/30 border border-rose-100 dark:border-rose-800 text-rose-600 dark:text-rose-400 text-xs sm:text-sm font-bold uppercase tracking-wide">
                  {t('landing.hero.badge')}
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black leading-tight dark:text-gray-100">
                  {t('landing.hero.title1')}
                  <br />
                  {t('landing.hero.title2')}{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 via-fuchsia-500 to-violet-600">
                    {t('landing.hero.title3')}
                  </span>
                </h1>
                <p className="text-sm sm:text-base md:text-lg text-gray-700 dark:text-gray-300 max-w-lg leading-relaxed">
                  {t('landing.hero.subtitle')}
                </p>
                <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
                  <Btn
                    onClick={openAuth}
                    icon={Users}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium shadow-lg shadow-violet-500/25 !bg-gradient-to-r !from-violet-500 !to-purple-600"
                    data-testid="landing-cta-inscription"
                  >
                    {t('landing.hero.cta')}
                  </Btn>
                  <Btn
                    onClick={openAuth}
                    icon={Heart}
                    v="outline"
                    className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium !border-rose-300 !text-rose-600 dark:!text-rose-400 hover:!bg-rose-50 dark:hover:!bg-rose-950/30"
                    data-testid="landing-cta-dating"
                  >
                    {t('landing.hero.ctaDating')}
                  </Btn>
                </div>
              </div>

              {/* Cartes flottantes desktop */}
              <div className="lg:w-1/2 relative hidden lg:block" style={{ minHeight: 460 }}>
                <div
                  className="animate-float absolute rounded-[22px] overflow-hidden bg-white dark:bg-gray-900"
                  style={{
                    top: 10,
                    left: '5%',
                    width: 210,
                    zIndex: 3,
                    border: '1px solid rgba(244,63,94,0.08)',
                    boxShadow: '0 20px 50px rgba(244,63,94,0.12)',
                  }}
                >
                  <div className="relative h-[150px] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80"
                      alt="Mai"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    <div className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-md">
                      <Heart size={14} className="text-rose-500" fill="#f43f5e" />
                    </div>
                  </div>
                  <div className="p-3.5">
                    <p className="font-extrabold text-[15px] text-gray-900 dark:text-gray-100">
                      Mai, 28
                    </p>
                    <p className="text-xs text-gray-400">Lyon - Vang</p>
                  </div>
                </div>
                <div
                  className="animate-float-delayed absolute bg-white dark:bg-gray-900 rounded-[20px] p-5"
                  style={{
                    top: -5,
                    right: '2%',
                    width: 195,
                    zIndex: 2,
                    border: '1px solid rgba(124,58,237,0.08)',
                    boxShadow: '0 20px 50px rgba(124,58,237,0.10)',
                  }}
                >
                  <div className="w-[42px] h-[42px] rounded-[13px] bg-gradient-to-br from-violet-200 to-violet-300 flex items-center justify-center mb-3">
                    <CalendarDays size={20} className="text-violet-600" />
                  </div>
                  <p className="font-extrabold text-base text-gray-900 dark:text-gray-100">
                    Nouvel An Hmong
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Mars 2026 - Lyon</p>
                  <div className="mt-2.5 inline-block px-3 py-1 rounded-full bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400 text-xs font-bold">
                    {t('landing.cards.registered', { count: 142 })}
                  </div>
                </div>
                <div
                  className="animate-float absolute bg-white dark:bg-gray-900 rounded-[18px] p-4"
                  style={{
                    bottom: 70,
                    left: '2%',
                    width: 195,
                    zIndex: 2,
                    border: '1px solid rgba(124,58,237,0.08)',
                    boxShadow: '0 20px 50px rgba(124,58,237,0.10)',
                  }}
                >
                  <div className="w-[42px] h-[42px] rounded-[13px] bg-gradient-to-br from-violet-200 to-violet-400 flex items-center justify-center mb-3">
                    <Users size={20} className="text-violet-700" />
                  </div>
                  <p className="font-extrabold text-base text-gray-900 dark:text-gray-100">
                    {t('landing.cards.friends')}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {t('landing.cards.friendsSub')}
                  </p>
                </div>
                <div
                  className="animate-float-delayed absolute bg-white dark:bg-gray-900 rounded-2xl px-4 py-3 flex items-center gap-2.5"
                  style={{
                    bottom: 30,
                    right: '5%',
                    zIndex: 3,
                    border: '1px solid rgba(244,63,94,0.06)',
                    boxShadow: '0 12px 35px rgba(244,63,94,0.10)',
                  }}
                >
                  <div className="w-[34px] h-[34px] rounded-full bg-gradient-to-br from-rose-300 to-rose-500 flex items-center justify-center flex-shrink-0">
                    <Heart size={14} fill="white" className="text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-gray-900 dark:text-gray-100">
                      {t('landing.cards.newMatch')}
                    </p>
                    <p className="text-[11px] text-gray-400">Pao & Mai</p>
                  </div>
                </div>
              </div>

              {/* Grille mobile */}
              <div className="lg:hidden grid grid-cols-2 gap-3 w-full max-w-sm mx-auto">
                {[
                  {
                    icon: Users,
                    title: t('landing.pillars.community.title'),
                    desc: t('landing.mobile.community'),
                    bg: 'bg-violet-50 dark:bg-violet-900/30',
                    ic: 'text-violet-500',
                    action: openAuth,
                  },
                  {
                    icon: Heart,
                    title: t('landing.pillars.dating.title'),
                    desc: t('landing.mobile.dating'),
                    bg: 'bg-rose-50 dark:bg-rose-900/30',
                    ic: 'text-rose-500',
                    action: openAuth,
                  },
                  {
                    icon: CalendarDays,
                    title: t('landing.pillars.events.title'),
                    desc: t('landing.mobile.events'),
                    bg: 'bg-amber-50 dark:bg-amber-900/30',
                    ic: 'text-amber-500',
                    action: () => navigate('/events'),
                  },
                  {
                    icon: ShieldCheck,
                    title: t('landing.traditions.clan.title').split(' ')[0],
                    desc: t('landing.mobile.traditions'),
                    bg: 'bg-emerald-50 dark:bg-emerald-900/30',
                    ic: 'text-emerald-500',
                    action: () => traditionsRef.current?.scrollIntoView({ behavior: 'smooth' }),
                  },
                ].map((c, i) => (
                  <Card key={i} className="p-4 cursor-pointer" hover onClick={c.action}>
                    <div
                      className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center mb-2`}
                    >
                      <c.icon size={20} className={c.ic} />
                    </div>
                    <h3 className="font-bold text-base dark:text-gray-100">{c.title}</h3>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{c.desc}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── 3 Piliers ── */}
        <section
          ref={piliersRef}
          data-testid="landing-piliers"
          className="container mx-auto px-4 py-10 sm:py-16"
        >
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 dark:text-gray-100">
              {t('landing.pillars.title')}
            </h2>
            <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 max-w-xl mx-auto">
              {t('landing.pillars.subtitle')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {pillars.map((p, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredPillar(i)}
                onMouseLeave={() => setHoveredPillar(null)}
                className={`rounded-xl p-4 sm:p-7 text-center cursor-pointer transition-all duration-300 border ${
                  hoveredPillar === i
                    ? `bg-gradient-to-br ${p.bgHover} border-transparent shadow-xl -translate-y-2`
                    : 'bg-white dark:bg-gray-900 border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md'
                }`}
                onClick={p.action}
              >
                <div
                  className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl mx-auto mb-3 sm:mb-4 flex items-center justify-center bg-gradient-to-br ${p.iconBg} shadow-lg`}
                  style={{ boxShadow: `0 8px 25px ${p.color}30` }}
                >
                  <p.icon size={24} className="text-white" />
                </div>
                <h3 className="text-base sm:text-xl font-semibold mb-2 dark:text-gray-100">
                  {p.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                  {p.desc}
                </p>
                <span
                  className="font-semibold text-sm flex items-center gap-1 mx-auto justify-center"
                  style={{ color: p.color }}
                >
                  {p.link} <ArrowRight size={14} />
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Respect des traditions ── */}
        <section
          ref={traditionsRef}
          className="hmong-pattern-bg pt-10 pb-14 sm:pt-16 sm:pb-20 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-b from-purple-50/[0.85] to-white/[0.85] dark:from-gray-900/[0.85] dark:to-gray-950/[0.85] z-0" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 dark:text-gray-100">
                {t('landing.traditions.title')}
              </h2>
              <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 max-w-xl mx-auto">
                {t('landing.traditions.subtitle')}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
              {traditions.map((tr, i) => (
                <div
                  key={i}
                  className="text-center bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-gray-800"
                >
                  <div
                    className={`w-12 h-12 rounded-full mx-auto mb-3 sm:mb-4 flex items-center justify-center ${
                      tr.color === 'rose'
                        ? 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                        : tr.color === 'emerald'
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                          : 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                    }`}
                  >
                    <tr.icon size={22} />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 dark:text-gray-100">
                    {tr.title}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                    {tr.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA final ── */}
        <section className="container mx-auto px-4 py-10 sm:py-16">
          <div
            className="filigrane-kotaw bg-gradient-to-r from-rose-500 via-fuchsia-500 to-violet-600 rounded-xl p-4 sm:p-8 md:p-12 text-center overflow-hidden relative"
            style={{ backgroundSize: '200% 200%', animation: 'gradient-shift 8s ease infinite' }}
          >
            <div className="absolute inset-0 opacity-[0.06]">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white" />
              <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white" />
            </div>
            <div className="relative">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-white">
                {t('landing.cta.title')}
              </h2>
              <p className="text-sm sm:text-base text-white/90 mb-6 max-w-lg mx-auto">
                {t('landing.cta.subtitle')}
              </p>
              <Btn
                v="white"
                onClick={openAuth}
                className="mx-auto px-5 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base"
                data-testid="landing-cta-inscription"
              >
                {t('landing.cta.button')}
              </Btn>
            </div>
          </div>
        </section>

        <PajNtaubBand height={5} />

        <Footer openAuth={openAuth} />

        <div className="h-20 lg:hidden" />
      </div>
    </div>
  );
};

export default LandingPage;
